// Phase 41.8 MVP — minimum-viable chat drawer.
//
// Right-rail drawer present on every authenticated page. Cmd/Ctrl+K toggles.
// State persists across navigation via localStorage (drawer-open + active
// session id). Calls /api/v1/chat endpoints directly (no SSE in MVP — uses
// the JSON driver, so responses arrive per-turn not token-by-token).
//
// Polish features deferred to follow-ups:
//   - SSE streaming (token-by-token assistant text)
//   - @-mention popover
//   - Diff renderer for batch writes (currently shows JSON)
//   - Transcript export button (endpoint exists; UI defer)
//   - Cost ticker chip (data exists; defer styling)
//   - Full-screen /chat page route

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { api, ApiError } from '../services/api';
import { useChatSnapshot } from '../contexts/ChatSnapshot';
import { useVoiceMode } from '../hooks/useVoiceMode';
import {
  type ChatStatus, type PageContext, type ChatMessage, type ChatSession,
  type ChatEvent, type PostMessageResult, type PendingApproval,
} from './chat/types';
import { LS_OPEN, LS_SESSION, readLS, writeLS, derivePageContext } from './chat/storage';
import { Message } from './chat/Message';
import { PendingChip } from './chat/PendingChip';
import { ChatHistorySkeleton } from './chat/Skeleton';

// ─── Component ───────────────────────────────────────────────────────

export function ChatDrawer(): ReactNode {
  const loc = useLocation();
  const params = useParams<{ id?: string }>();
  const snapshot = useChatSnapshot();
  const pageContext = useMemo(() => {
    const base = derivePageContext(loc.pathname, params as Record<string, string | undefined>);
    // Innovation Audit #9 — merge in the live snapshot from whichever tab
    // has published one. ChatDrawer doesn't care which tab — it just
    // forwards whatever it sees to the server.
    return snapshot ? { ...base, snapshot } : base;
  }, [loc.pathname, params, snapshot]);

  const [open, setOpen] = useState<boolean>(() => readLS(LS_OPEN) === '1');
  const [sessionId, setSessionId] = useState<string | null>(() => readLS(LS_SESSION));
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // BUG A fix — when ensureSession creates a brand-new session, the
  // sessionId-change useEffect would fire a GET that returned [] and wiped
  // the optimistic user message that sendMessage had just pushed. Track
  // freshly-created sessionIds in this ref so the load effect skips its
  // fetch when it KNOWS the session is empty.
  const freshlyCreatedSessionRef = useRef<string | null>(null);
  const [status, setStatus] = useState<ChatStatus>('IDLE');
  const [costUsd, setCostUsd] = useState<number>(0);
  const [budgetUsd, setBudgetUsd] = useState<number>(5);
  const [pending, setPending] = useState<PendingApproval[]>([]);
  const [draft, setDraft] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Array<{
    fileId: string; filename: string; summary: string;
    suggestedIntent: string; intentConfidence: number;
    targetProjectSignals: string[];
  }>>([]);
  const [uploadingCount, setUploadingCount] = useState<number>(0);
  const [dragActive, setDragActive] = useState<boolean>(false);
  // True while fetching a saved session's transcript — drives the skeleton.
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Innovation Audit #16 — browser-native voice mode. Push-to-talk via
  // Web Speech API; the final transcript becomes the next message and
  // auto-sends. TTS playback (optional, toggle in the input bar) reads
  // the assistant's reply aloud as deltas stream.
  const voice = useVoiceMode({
    onFinalTranscript: (text) => {
      // Stop any in-flight TTS so the user's question isn't talked over.
      voice.stopSpeaking();
      // Replace the draft and trigger send via the existing event hook.
      setDraft(text);
      // Defer one frame so React commits the draft before the send fires.
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('ub-chat-send'));
      }, 30);
    },
    onError: (msg) => setError(msg),
  });

  // Persist drawer-open + session-id changes.
  useEffect(() => { writeLS(LS_OPEN, open ? '1' : '0'); }, [open]);
  useEffect(() => { writeLS(LS_SESSION, sessionId); }, [sessionId]);

  // Innovation Audit #13 — speculative prewarm. When the drawer opens on
  // a project page, fire a /chat/sessions/prewarm so the worker warms the
  // KV memo cache (from audit #8) with the 3 most-likely first-turn reads.
  // Skip if we already prewarmed this projectId in the last 60s.
  const prewarmedFor = useRef<{ projectId: string; at: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    const projectId = pageContext.projectId;
    if (!projectId) return;
    const now = Date.now();
    const last = prewarmedFor.current;
    if (last && last.projectId === projectId && (now - last.at) < 60_000) return;
    prewarmedFor.current = { projectId, at: now };
    api.post<{ ok: boolean; prewarmed: number }>('/chat/sessions/prewarm', { projectId })
      .catch(() => { /* fire-and-forget — failure means tools run cold, no biggie */ });
  }, [open, pageContext.projectId]);

  // Hotkey: Cmd/Ctrl+K toggles drawer. Ignore when an editable element has focus
  // (so the user can type Cmd+K inside other inputs without losing focus weirdly —
  // but our SAME hotkey on top-level should always work; balance via shift).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Phase 45.6 / 45.7 / 45.8 / 45.9 — programmatic chat opener.
  // Other components dispatch a CustomEvent('ub-chat-open-with', {detail:{message, autoSend?}})
  // to open the drawer and (optionally) submit a message — e.g. SpecsTab's
  // "Review interactively" button, SheetsTab's "Reclassify", BOM "Why?",
  // FlagsTab's "Bulk triage".
  useEffect(() => {
    function onOpenWith(ev: Event) {
      const detail = (ev as CustomEvent<{ message?: string; autoSend?: boolean }>).detail;
      const msg = detail?.message?.trim();
      if (!msg) return;
      setOpen(true);
      setDraft(msg);
      if (detail?.autoSend) {
        // Defer one frame to let setDraft commit + textarea ref settle.
        setTimeout(() => {
          // Best-effort programmatic send. If status guards prevent it, the
          // user can still hit Send manually since the draft is in place.
          window.dispatchEvent(new CustomEvent('ub-chat-send'));
        }, 60);
      }
    }
    window.addEventListener('ub-chat-open-with', onOpenWith as EventListener);
    return () => window.removeEventListener('ub-chat-open-with', onOpenWith as EventListener);
  }, []);

  // Companion listener for the autoSend half — triggers the existing send
  // flow without exposing internal state to the outside world.
  useEffect(() => {
    function onSendEvt() {
      // We can't call sendMessage() directly here because it's defined later
      // in the function scope. Instead, simulate Cmd+Enter on the textarea.
      const ta = document.querySelector<HTMLTextAreaElement>('aside textarea');
      if (!ta) return;
      ta.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', metaKey: true, bubbles: true }));
    }
    window.addEventListener('ub-chat-send', onSendEvt);
    return () => window.removeEventListener('ub-chat-send', onSendEvt);
  }, []);

  // Load existing session messages on mount / when sessionId changes.
  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      setStatus('IDLE');
      setCostUsd(0);
      setPending([]);
      return;
    }
    // BUG A fix — skip the load when this sessionId was just created in
    // memory. The server-side row is empty by definition, and fetching it
    // here would race with sendMessage's optimistic user-message push and
    // wipe it.
    if (freshlyCreatedSessionRef.current === sessionId) {
      freshlyCreatedSessionRef.current = null;
      return;
    }
    let cancelled = false;
    setLoadingHistory(true);
    (async () => {
      try {
        const data = await api.get<{ session: ChatSession; messages: ChatMessage[] }>(`/chat/sessions/${sessionId}`);
        if (cancelled) return;
        setMessages(data.messages ?? []);
        setStatus(data.session.status);
        setCostUsd(data.session.totalCostUsd);
        // Pending approvals: surface via audit-log query (cheap since we cap at 20).
        const audit = await api.get<{ entries: Array<{ id: string; tool_name: string; was_approved: boolean; applied_at: string | null }> }>(
          `/chat/sessions/${sessionId}`,
        ).catch(() => null);
        // The /:id endpoint doesn't return pending audits — derive from data.session.status.
        if (data.session.status === 'AWAITING_APPROVAL') {
          // We don't have a dedicated pending-list endpoint in MVP; ask the user to
          // re-send to refresh or treat status as the signal. Best-effort: pull from
          // last assistant message's tool_use blocks.
          const lastAsst = [...(data.messages ?? [])].reverse().find((m) => m.role === 'assistant');
          if (lastAsst) {
            const tus = lastAsst.content.filter((c) => c.type === 'tool_use');
            // We don't have audit IDs here. Defer real pending state to the response of the
            // next /messages or /approve call which DO surface auditId in pending_approval events.
            void tus; void audit;
          }
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          // Session no longer exists — clear and start fresh.
          setSessionId(null);
        } else {
          setError(err instanceof Error ? err.message : 'load_failed');
        }
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  // Innovation Audit #10 — refresh canonical transcript only on tab-focus or
  // drawer-open (insurance for the rare case where the SSE connection
  // dropped mid-turn). Skip when a turn is in flight.
  useEffect(() => {
    function onFocus() {
      if (!sessionId) return;
      if (status === 'WORKING') return;
      api.get<{ session: ChatSession; messages: ChatMessage[] }>(`/chat/sessions/${sessionId}`).then((data) => {
        setMessages(data.messages ?? []);
        if (data.session.status) setStatus(data.session.status);
        setCostUsd(data.session.totalCostUsd);
      }).catch(() => { /* ignore */ });
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [sessionId, status]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const created = await api.post<ChatSession>('/chat/sessions', {
      title: 'Chat',
      pageContext,
      projectId: pageContext.projectId ?? null,
    });
    // BUG A fix — flag this id so the load useEffect that fires on
    // sessionId change skips the fetch (the row is empty by definition).
    freshlyCreatedSessionRef.current = created.id;
    setSessionId(created.id);
    return created.id;
  }, [sessionId, pageContext]);

  const uploadFile = useCallback(async (file: File) => {
    if (file.size > 50 * 1024 * 1024) {
      setError(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB) — max 50 MB`);
      return;
    }
    if (file.type !== 'application/pdf') {
      setError(`Only PDF uploads are supported (got ${file.type || 'unknown'})`);
      return;
    }
    setError(null);
    setUploadingCount((n) => n + 1);
    try {
      const sid = await ensureSession();
      const API_BASE = import.meta.env.DEV
        ? '/api/v1'
        : 'https://ultimate-bidder-api.frankpedersen84.workers.dev/api/v1';
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${API_BASE}/chat/sessions/${sid}/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new Error(`HTTP ${resp.status}: ${body.slice(0, 200) || resp.statusText}`);
      }
      const data = await resp.json() as {
        file_id: string;
        filename: string;
        summary: string;
        suggested_intent: string;
        intent_confidence: number;
        target_project_signals: string[];
      };
      setAttachments((prev) => [...prev, {
        fileId: data.file_id,
        filename: data.filename,
        summary: data.summary || '',
        suggestedIntent: data.suggested_intent,
        intentConfidence: data.intent_confidence,
        targetProjectSignals: data.target_project_signals ?? [],
      }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'upload_failed');
    } finally {
      setUploadingCount((n) => Math.max(0, n - 1));
    }
  }, [ensureSession]);

  const onFilesPicked = useCallback((files: FileList | null) => {
    if (!files) return;
    for (const f of Array.from(files)) {
      void uploadFile(f);
    }
  }, [uploadFile]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    onFilesPicked(e.dataTransfer.files);
  }, [onFilesPicked]);

  const removeAttachment = useCallback((fileId: string) => {
    setAttachments((prev) => prev.filter((a) => a.fileId !== fileId));
  }, []);

  const sendMessage = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;
    if (busy) return;
    setError(null);
    setBusy(true);
    setStatus('WORKING');
    try {
      const sid = await ensureSession();
      // Optimistic user message in the UI.
      const optimistic: ChatMessage = {
        id: `tmp_${Math.random()}`,
        seq: messages.length,
        role: 'user',
        content: [{ type: 'text', text }],
        costUsd: 0,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);
      setDraft('');
      setPending([]);
      // Clear attachments — they've been sent into this turn's user message.
      setAttachments([]);

      // Stream events via SSE so we don't depend on the worker completing the
      // entire loop within the browser's fetch timeout. The server-side
      // route dispatches on Accept: text/event-stream.
      const API_BASE = import.meta.env.DEV
        ? '/api/v1'
        : 'https://ultimate-bidder-api.frankpedersen84.workers.dev/api/v1';
      const messageBody: Record<string, unknown> = { message: text, pageContext };
      if (attachments.length > 0) {
        messageBody.attachments = attachments.map((a) => ({
          fileId: a.fileId,
          filename: a.filename,
          summary: a.summary || undefined,
        }));
      }
      const resp = await fetch(`${API_BASE}/chat/sessions/${sid}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(messageBody),
      });

      if (!resp.ok) {
        let bodyText = '';
        try { bodyText = await resp.text(); } catch { /* ignore */ }
        throw new Error(`HTTP ${resp.status}: ${bodyText.slice(0, 300) || resp.statusText}`);
      }
      if (!resp.body) {
        throw new Error('no_response_body');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      const pendingAccum: PendingApproval[] = [];

      // Track the current assistant "bubble" being streamed so multiple
      // assistant_text events in one turn append rather than create new bubbles.
      // Simplest: refresh transcript at stream end. While streaming, append
      // each assistant_text as a new bubble so the UI feels alive.
      const streamedBubbles: ChatMessage[] = [];

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        // SSE event delimiter is a blank line.
        let sep: number;
        while ((sep = buf.indexOf('\n\n')) >= 0) {
          const rawEvent = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          const dataLine = rawEvent.split('\n').find((l) => l.startsWith('data:'));
          if (!dataLine) continue;
          const json = dataLine.slice(5).trim();
          if (!json) continue;
          let evt: ChatEvent;
          try {
            evt = JSON.parse(json) as ChatEvent;
          } catch {
            continue;
          }

          if (evt.type === 'assistant_text_delta' && typeof evt.text === 'string' && evt.text.length > 0 && evt.messageId) {
            // Innovation Audit #1 — token-delta streaming. Find-or-create a
            // bubble keyed by messageId and append the delta to its accumulated
            // text. The user sees the assistant typing in real time.
            const msgId = evt.messageId;
            const delta = evt.text;
            // Innovation Audit #16 — feed deltas to TTS (no-op when off).
            voice.speakChunk(delta);
            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === msgId);
              if (idx >= 0) {
                const next = [...prev];
                const existing = next[idx];
                const newText =
                  (existing.content[0]?.type === 'text' && typeof existing.content[0].text === 'string'
                    ? existing.content[0].text
                    : '') + delta;
                next[idx] = {
                  ...existing,
                  content: [{ type: 'text', text: newText }],
                };
                return next;
              }
              // First delta for this messageId — create the bubble.
              const bubble: ChatMessage = {
                id: msgId,
                seq: prev.length,
                role: 'assistant',
                content: [{ type: 'text', text: delta }],
                costUsd: 0,
                createdAt: new Date().toISOString(),
              };
              streamedBubbles.push(bubble);
              return [...prev, bubble];
            });
          } else if (evt.type === 'assistant_text_done' && typeof evt.text === 'string' && evt.messageId) {
            // Final canonical text — reconcile any drift from delta accumulation.
            const msgId = evt.messageId;
            const finalText = evt.text;
            // Innovation Audit #16 — flush any TTS buffer so the last
            // sentence is spoken even without a trailing punctuation hit.
            voice.speakChunk('', { flush: true });
            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === msgId);
              if (idx < 0) {
                // No deltas arrived (rare) — create the bubble now.
                return [...prev, {
                  id: msgId,
                  seq: prev.length,
                  role: 'assistant',
                  content: [{ type: 'text', text: finalText }],
                  costUsd: 0,
                  createdAt: new Date().toISOString(),
                }];
              }
              const next = [...prev];
              next[idx] = { ...next[idx], content: [{ type: 'text', text: finalText }] };
              return next;
            });
          } else if (evt.type === 'assistant_text' && typeof evt.text === 'string' && evt.text.length > 0) {
            // Legacy whole-bubble path (kept for JSON-mode + transcript replay).
            // Innovation Audit #16 — speak the whole bubble at once.
            voice.speakChunk(evt.text, { flush: true });
            const bubble: ChatMessage = {
              id: evt.messageId ?? `streamed_${Math.random()}`,
              seq: messages.length + streamedBubbles.length + 1,
              role: 'assistant',
              content: [{ type: 'text', text: evt.text }],
              costUsd: 0,
              createdAt: new Date().toISOString(),
            };
            streamedBubbles.push(bubble);
            setMessages((prev) => [...prev, bubble]);
          } else if (evt.type === 'tool_use_starting' && evt.callId) {
            // Innovation Audit #6 — fine-grained tool streaming. Render a
            // skeleton bubble for the tool call the instant it begins, so the
            // user sees "🔧 calling read_bom_items…" without waiting for the
            // model to finish generating the input JSON.
            const bubbleId = `streamed_tu_${evt.callId}`;
            const existing = streamedBubbles.find((b) => b.id === bubbleId);
            if (!existing) {
              const bubble: ChatMessage = {
                id: bubbleId,
                seq: messages.length + streamedBubbles.length + 1,
                role: 'assistant',
                content: [{ type: 'tool_use', name: evt.toolName, input: {}, id: evt.callId }],
                costUsd: 0,
                createdAt: new Date().toISOString(),
              };
              streamedBubbles.push(bubble);
              setMessages((prev) => [...prev, bubble]);
            }
          } else if (evt.type === 'tool_input_delta' && evt.callId && typeof evt.partial === 'string') {
            // Append the streamed JSON fragment to the bubble's tool_use input
            // as a `_partial_json` string. The Message renderer detects this
            // and shows the live JSON until the final tool_use event lands
            // with the parsed input.
            const bubbleId = `streamed_tu_${evt.callId}`;
            const partial = evt.partial;
            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === bubbleId);
              if (idx < 0) return prev;
              const next = [...prev];
              const existing = next[idx];
              const tu = existing.content[0];
              if (tu?.type === 'tool_use') {
                const prevPartial = ((tu.input as { _partial_json?: string } | undefined)?._partial_json) ?? '';
                next[idx] = {
                  ...existing,
                  content: [{
                    ...tu,
                    input: { _partial_json: prevPartial + partial },
                  }],
                };
              }
              return next;
            });
          } else if (evt.type === 'tool_use') {
            // Final tool_use carrying the parsed input. Reconcile any bubble
            // we created from tool_use_starting; otherwise create a new one.
            const bubbleId = `streamed_tu_${evt.callId}`;
            const existingIdx = streamedBubbles.findIndex((b) => b.id === bubbleId);
            if (existingIdx >= 0) {
              setMessages((prev) => {
                const idx = prev.findIndex((m) => m.id === bubbleId);
                if (idx < 0) return prev;
                const next = [...prev];
                next[idx] = {
                  ...next[idx],
                  content: [{ type: 'tool_use', name: evt.toolName, input: evt.input, id: evt.callId }],
                };
                return next;
              });
            } else {
              const bubble: ChatMessage = {
                id: bubbleId,
                seq: messages.length + streamedBubbles.length + 1,
                role: 'assistant',
                content: [{ type: 'tool_use', name: evt.toolName, input: evt.input, id: evt.callId }],
                costUsd: 0,
                createdAt: new Date().toISOString(),
              };
              streamedBubbles.push(bubble);
              setMessages((prev) => [...prev, bubble]);
            }
          } else if (evt.type === 'pending_approval') {
            pendingAccum.push({
              callId: evt.callId ?? '',
              auditId: evt.auditId ?? '',
              toolName: evt.toolName ?? '?',
              input: evt.input,
              summary: evt.summary ?? `Apply ${evt.toolName ?? '?'}`,
            });
            setPending([...pendingAccum]);
          } else if (evt.type === 'cost') {
            if (typeof evt.sessionCostUsd === 'number') setCostUsd(evt.sessionCostUsd);
          } else if (evt.type === 'turn_start') {
            if (typeof evt.budgetUsd === 'number') setBudgetUsd(evt.budgetUsd);
          } else if (evt.type === 'turn_end') {
            if (evt.status) setStatus(evt.status);
            if (evt.reason && (evt.status === 'ERROR' || evt.status === 'BUDGET_EXHAUSTED')) {
              setError(evt.reason);
            }
          }
        }
      }

      // BUG D fix — reconcile streamed-bubble fragments back to the canonical
      // server view. During streaming we create separate bubbles per
      // tool_use_starting event + per assistant_text_delta block, which leaves
      // 4+ fragmented bubbles per turn. The server's canonical transcript has
      // ONE assistant message per Anthropic turn (text + tool_use blocks
      // merged into a single content array). Refetch and replace AFTER
      // setBusy(false) so the user can type immediately, but the view
      // consolidates to the clean canonical structure within a few hundred ms.
      const sidForRefetch = sid;
      void Promise.resolve().then(async () => {
        try {
          const refreshed = await api.get<{ session: ChatSession; messages: ChatMessage[] }>(
            `/chat/sessions/${sidForRefetch}`,
          );
          setMessages(refreshed.messages ?? []);
          if (refreshed.session.totalCostUsd) setCostUsd(refreshed.session.totalCostUsd);
          if (refreshed.session.status) setStatus(refreshed.session.status);
        } catch {
          // Non-fatal — the streamed view is good enough.
        }
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'send_failed';
      setError(msg);
      setStatus('ERROR');
    } finally {
      setBusy(false);
    }
  }, [draft, busy, ensureSession, messages.length, pageContext]);

  const approve = useCallback(async (auditId: string, decision: 'apply' | 'skip') => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      await api.post<{ ok: boolean; status: ChatStatus; remaining_pending: number; result: unknown }>(
        `/chat/sessions/${sessionId}/approve`,
        { audit_id: auditId, decision },
      );
      // Refresh state.
      const refreshed = await api.get<{ session: ChatSession; messages: ChatMessage[] }>(`/chat/sessions/${sessionId}`);
      setMessages(refreshed.messages ?? []);
      setStatus(refreshed.session.status);
      setCostUsd(refreshed.session.totalCostUsd);
      setPending((prev) => prev.filter((p) => p.auditId !== auditId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'approve_failed');
    } finally {
      setBusy(false);
    }
  }, [sessionId]);

  const abort = useCallback(async () => {
    if (!sessionId) return;
    // Innovation Audit #16 — cut TTS playback on user abort so the model
    // doesn't keep talking after the user clicked Stop.
    voice.stopSpeaking();
    try {
      await api.post<{ ok: boolean }>(`/chat/sessions/${sessionId}/abort`, {});
      setStatus('ABORTED');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'abort_failed');
    }
  }, [sessionId, voice]);

  const startNewSession = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    setStatus('IDLE');
    setCostUsd(0);
    setPending([]);
    setError(null);
  }, []);

  return (
    <>
      {/* Floating toggle button */}
      <button
        aria-label="Toggle chat (Cmd+K)"
        aria-expanded={open}
        aria-controls="bid-copilot-drawer"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 rounded-full bg-brand-navy text-white shadow-lg w-12 h-12 flex items-center justify-center hover:scale-105 transition"
      >
        💬
      </button>

      {/* Backdrop (very subtle — drawer is non-modal) */}
      {open && (
        <div
          aria-hidden
          className="fixed inset-0 z-30 pointer-events-none"
        />
      )}

      {/* Drawer */}
      <aside
        id="bid-copilot-drawer"
        aria-labelledby="bid-copilot-title"
        className={`fixed top-0 right-0 z-30 h-screen w-full sm:w-[440px] bg-white shadow-xl border-l border-slate-200 transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}
        aria-hidden={!open}
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragOver={(e) => { e.preventDefault(); }}
        onDragLeave={(e) => {
          // Only clear if leaving the entire drawer, not entering a child.
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setDragActive(false);
        }}
        onDrop={onDrop}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 bg-brand-navy text-white flex items-center gap-2">
          <span id="bid-copilot-title" className="font-semibold">Bid Copilot</span>
          <span className="text-xs text-slate-300 ml-2">
            {pageContext.route}{pageContext.projectId ? ` · ${pageContext.projectId.slice(0, 12)}…` : ''}
          </span>
          <span className="ml-auto text-xs text-slate-300">
            ${costUsd.toFixed(2)} / ${budgetUsd.toFixed(2)}
          </span>
          <button
            onClick={startNewSession}
            className="ml-2 text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20"
            title="Start a new session"
          >
            + New
          </button>
          <button aria-label="Close chat" onClick={() => setOpen(false)} className="ml-1 text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20">
            ✕
          </button>
        </div>

        {/* Status strip */}
        <div role="status" aria-live="polite" className={`px-4 py-1 text-xs ${
          status === 'IDLE' ? 'bg-slate-50 text-slate-600' :
          status === 'WORKING' ? 'bg-amber-50 text-amber-700' :
          status === 'AWAITING_APPROVAL' ? 'bg-blue-50 text-blue-700' :
          status === 'BUDGET_EXHAUSTED' ? 'bg-rose-50 text-rose-700' :
          status === 'ERROR' ? 'bg-rose-50 text-rose-700' :
          status === 'ABORTED' ? 'bg-slate-100 text-slate-600' :
          'bg-slate-50 text-slate-600'
        }`}>
          {status === 'IDLE' && (sessionId ? 'Ready' : 'Type a message to start')}
          {status === 'WORKING' && (
            <span>Working… <button onClick={abort} className="ml-2 underline">Stop</button></span>
          )}
          {status === 'AWAITING_APPROVAL' && `Awaiting your approval (${pending.length})`}
          {status === 'BUDGET_EXHAUSTED' && 'Budget exhausted — start a new session.'}
          {status === 'ERROR' && (error ?? 'Error')}
          {status === 'ABORTED' && 'Aborted'}
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          role="log"
          aria-label="Conversation"
          aria-live="polite"
          aria-busy={loadingHistory}
          className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm"
        >
          {loadingHistory && messages.length === 0 && <ChatHistorySkeleton />}
          {messages
            .filter((m) => {
              // Render system_meta rows ONLY when they carry a 'brief' marker —
              // those are renderable artifacts. Plain system_meta rows (history
              // summarizer fold-downs from Phase 41.2 C3) stay hidden.
              if (m.role !== 'system_meta') return true;
              const tc = (m as { toolCalls?: { kind?: string } | null }).toolCalls;
              return tc && tc.kind === 'brief';
            })
            .map((m) => (
              <Message key={m.id} msg={m} />
            ))}
          {pending.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-200">
              {pending.map((p) => (
                <PendingChip key={p.auditId} approval={p} onApply={() => approve(p.auditId, 'apply')} onSkip={() => approve(p.auditId, 'skip')} busy={busy} />
              ))}
            </div>
          )}
          {error && (
            <div className="text-rose-700 text-xs bg-rose-50 rounded p-2">{error}</div>
          )}
        </div>

        {/* Drag-active overlay */}
        {dragActive && (
          <div className="absolute inset-0 z-50 bg-brand-navy/80 text-white flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className="text-3xl mb-2">📎</div>
              <div className="text-sm font-semibold">Drop PDF to attach</div>
              <div className="text-xs text-slate-200 mt-1">Max 50 MB · PDF only</div>
            </div>
          </div>
        )}

        {/* Attachment chips */}
        {(attachments.length > 0 || uploadingCount > 0) && (
          <div className="border-t border-slate-200 px-3 py-2 space-y-1 max-h-40 overflow-y-auto">
            {attachments.map((a) => {
              const projectMismatch = pageContext.projectId
                && a.targetProjectSignals.length > 0
                && a.intentConfidence > 0.6
                && !a.targetProjectSignals.some((s) =>
                  s.toLowerCase().includes((pageContext.projectId ?? '').toLowerCase()),
                );
              return (
                <div key={a.fileId} className="text-xs border border-slate-300 rounded p-2 bg-slate-50">
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500">📎</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono truncate text-slate-700" title={a.filename}>{a.filename}</div>
                      {a.summary && <div className="text-slate-600 mt-0.5">{a.summary}</div>}
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        intent: {a.suggestedIntent} ({(a.intentConfidence * 100).toFixed(0)}% conf)
                        {a.targetProjectSignals.length > 0 && (
                          <> · mentions: {a.targetProjectSignals.slice(0, 3).join(', ')}</>
                        )}
                      </div>
                      {projectMismatch && (
                        <div className="text-amber-700 bg-amber-50 border border-amber-300 rounded mt-1 px-1 py-0.5 text-[11px]">
                          ⚠ This doc mentions project names that don't match the current page. Continue with caution.
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeAttachment(a.fileId)}
                      className="text-slate-400 hover:text-slate-700"
                      title="Remove attachment"
                    >✕</button>
                  </div>
                </div>
              );
            })}
            {uploadingCount > 0 && (
              <div className="text-xs text-slate-500 italic">Uploading {uploadingCount} file{uploadingCount === 1 ? '' : 's'}…</div>
            )}
          </div>
        )}

        {/* Input */}
        <div className="border-t border-slate-200 px-3 py-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            multiple
            className="hidden"
            onChange={(e) => onFilesPicked(e.target.files)}
          />
          <div className="relative">
            <textarea
              aria-label="Message Bid Copilot"
              value={
                // Innovation Audit #16 — while recording, show the live
                // transcript inline so the user sees their words appear.
                voice.state === 'recording'
                  ? (draft + (voice.interimTranscript ? ` ${voice.interimTranscript}` : ''))
                  : draft
              }
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              disabled={busy || status === 'WORKING' || voice.state === 'recording'}
              placeholder={
                status === 'AWAITING_APPROVAL'
                  ? 'Approve / skip the pending action above first…'
                  : voice.state === 'recording'
                  ? '🎙 Listening… release to send'
                  : 'Ask me anything… (Cmd+Enter to send)'
              }
              rows={3}
              className={`w-full resize-none rounded border px-2 py-1 text-sm focus:outline-none focus:ring focus:border-brand-navy disabled:bg-slate-100 ${
                voice.state === 'recording' ? 'border-rose-300 bg-rose-50' : 'border-slate-300'
              }`}
            />
          </div>
          <div className="flex items-center justify-between mt-1 gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={busy || uploadingCount > 0 || status === 'WORKING'}
              title="Attach PDF (or drag-drop anywhere in the drawer)"
              className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              📎 Attach
            </button>
            {/* Innovation Audit #16 — browser-native voice mode (Web Speech API).
                Hold mic to talk; release to send. Optional TTS toggle reads the
                assistant's reply aloud. Works in Chrome / Edge with zero backend
                setup. Future Cloudflare RealtimeKit upgrade documented in
                docs/VOICE-MODE.md will replace this with sub-800ms edge audio. */}
            {voice.supported ? (
              <>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); voice.start(); }}
                  onMouseUp={(e) => { e.preventDefault(); voice.stop(); }}
                  onMouseLeave={() => {
                    // If the user drags off the button while holding, treat as
                    // cancel — don't send a partial transcript.
                    if (voice.state === 'recording') voice.cancel();
                  }}
                  onTouchStart={(e) => { e.preventDefault(); voice.start(); }}
                  onTouchEnd={(e) => { e.preventDefault(); voice.stop(); }}
                  onTouchCancel={() => voice.cancel()}
                  disabled={busy || status === 'WORKING' || status === 'AWAITING_APPROVAL'}
                  title="Hold to talk (Web Speech)"
                  className={`text-xs px-2 py-1 rounded border select-none ${
                    voice.state === 'recording'
                      ? 'bg-rose-600 text-white border-rose-700 animate-pulse'
                      : 'border-slate-300 text-slate-600 hover:bg-slate-100'
                  } disabled:opacity-50`}
                >
                  {voice.state === 'recording' ? '🔴 Listening…' : '🎙 Hold to talk'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (voice.ttsEnabled) voice.stopSpeaking();
                    voice.setTtsEnabled(!voice.ttsEnabled);
                  }}
                  title={voice.ttsEnabled ? 'Read replies aloud (click to mute)' : 'Read replies aloud'}
                  className={`text-xs px-2 py-1 rounded border ${
                    voice.ttsEnabled
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                      : 'border-slate-300 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {voice.ttsEnabled ? '🔊 TTS' : '🔇 TTS'}
                </button>
              </>
            ) : (
              <button
                type="button"
                title="Voice requires Chrome or Edge on desktop. See docs/VOICE-MODE.md for the Cloudflare RealtimeKit upgrade path."
                disabled
                className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-400 disabled:opacity-50"
              >
                🎙 Voice (unsupported)
              </button>
            )}
            <span className="text-xs text-slate-400 ml-1 mr-auto truncate">
              {sessionId ? `${sessionId.slice(0, 12)}…` : 'no session'}
            </span>
            <button
              onClick={sendMessage}
              disabled={busy || (!draft.trim() && attachments.length === 0) || status === 'WORKING' || uploadingCount > 0}
              className="px-3 py-1 rounded bg-brand-navy text-white text-sm disabled:opacity-50 hover:bg-brand-navy/90"
            >
              Send
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
